import { inject, Injectable } from '@angular/core';
import { ToroEnums } from '@app/common/enumerations/toro.enums';
import { TranslateService } from '@ngx-translate/core';
import LanguageCode = ToroEnums.LanguageCode;
import { catchError, EMPTY, forkJoin } from 'rxjs';
import { IdentityApiService } from '@app/core/services/identity.service';
import { LocalCacheService } from '@app/common/services/local-cache.service';
import { UserPreferenceManagerService } from '@app/api/user-preference/user-preference-manager.service';
import { UserProfileManagerService } from '@app/api/user-profile/user-profile-manager.service';

@Injectable({
    providedIn: 'root'
})
export class AppInitializerService {
    private translateService = inject(TranslateService);
    private identity = inject(IdentityApiService);
    private localCache = inject(LocalCacheService);
    private userProfileManager = inject(UserProfileManagerService);
    private userPreferenceManager = inject(UserPreferenceManagerService);

    load() {
        this.warmSession();

        return new Promise((resolve, reject) => {
            this.translateService.addLangs([
                LanguageCode.English_US
                // , LanguageCode.Spanish, LanguageCode.French, LanguageCode.German, LanguageCode.Italian, LanguageCode.Portuguese,
                // LanguageCode.Dutch, LanguageCode.Swedish, LanguageCode.Japanese, LanguageCode.Korean, LanguageCode.Chinese_Simplified,
                // LanguageCode.Vietnamese, LanguageCode.Russian, LanguageCode.Bulgarian, LanguageCode.Danish, LanguageCode.Norwegian, LanguageCode.Czech
            ]);
            this.translateService.setFallbackLang(LanguageCode.English_US); // v18: setDefaultLang renamed

            // We need to wait for the strings to be loaded, too (ngx-translate 17 removed getTranslation;
            // reloadLang is the v17 fetch-and-store equivalent). We load all supported languages.
            const todo = this.translateService.getLangs().map((lang) => this.translateService.reloadLang(lang));

            // Do all the retrievals and resolve our promise when complete.
            forkJoin(todo)
                .subscribe(() => {
                    resolve(true);
                });
        });
    }

    /**
     * Starts the session reads the auth guard makes, so they run alongside the translation fetch instead of after
     * bootstrap and the first navigation. Only with a stored token: a logged-out visitor would just collect a 401.
     * Nothing waits on it. The guard finds isLoggedIn already true, or its own identity read joins this one in flight
     * (the interceptor shares identical GETs), and a failure here is the guard's to handle when it asks again.
     */
    private warmSession(): void {
        if (!this.localCache.iCentralApiAccessToken) { return; }

        this.identity.getIcentralUserDetails()
            .pipe(catchError(() => EMPTY))
            .subscribe(response => {
                if (response.body) { this.userProfileManager.cacheCurrentUser(response.body); }
            });
        this.userPreferenceManager.getUserPreferences()
            .pipe(catchError(() => EMPTY))
            .subscribe();
    }
}
